import { ResolvedAgentUiActionSchema, type ResolvedAgentUiAction } from '@hector/shared-types';
import { z } from 'zod';
import type { GlobalAgentUiAction } from './dispatcher';

export type CaseBoundGlobalAgentUiAction = Extract<GlobalAgentUiAction, { caseId: string }>;

const AgentUiActionHandoffStateSchema = z.object({
  agentUiActionHandoff: z.object({
    handoffId: z.string().uuid(),
    action: ResolvedAgentUiActionSchema,
  }).strict(),
}).strict();

export interface AgentUiActionHandoffState {
  readonly agentUiActionHandoff: {
    readonly handoffId: string;
    readonly action: CaseBoundGlobalAgentUiAction;
  };
}

function isCaseBoundGlobalAction(action: ResolvedAgentUiAction): action is CaseBoundGlobalAgentUiAction {
  return action.id === 'documents.preview'
    || action.id === 'bordereau.open_selection_for_stamping'
    || action.id === 'case_analysis.open_section';
}

export function createAgentUiActionHandoffState(
  action: CaseBoundGlobalAgentUiAction,
  handoffId: string = crypto.randomUUID(),
): AgentUiActionHandoffState {
  const parsed = AgentUiActionHandoffStateSchema.parse({ agentUiActionHandoff: { handoffId, action } });
  if (!isCaseBoundGlobalAction(parsed.agentUiActionHandoff.action)) {
    throw new Error(`Cannot create route handoff for non-case global action: actionId=${parsed.agentUiActionHandoff.action.id}.`);
  }
  return { agentUiActionHandoff: { handoffId: parsed.agentUiActionHandoff.handoffId, action: parsed.agentUiActionHandoff.action } };
}

export interface ConsumedAgentUiActionHandoff {
  readonly action: CaseBoundGlobalAgentUiAction | null;
  readonly shouldClear: boolean;
}

export function consumeAgentUiActionHandoff(
  state: unknown,
  routeCaseId: string,
  consumedHandoffIds: Set<string>,
): ConsumedAgentUiActionHandoff {
  const candidate = AgentUiActionHandoffStateSchema.safeParse(state);
  if (!candidate.success) {
    const carriesHandoff = typeof state === 'object' && state !== null && 'agentUiActionHandoff' in state;
    return { action: null, shouldClear: carriesHandoff };
  }
  const { handoffId, action } = candidate.data.agentUiActionHandoff;
  if (!isCaseBoundGlobalAction(action) || action.caseId !== routeCaseId || consumedHandoffIds.has(handoffId)) {
    return { action: null, shouldClear: true };
  }
  // Mark synchronously before React can replay an effect in StrictMode.
  consumedHandoffIds.add(handoffId);
  return { action, shouldClear: true };
}
